import { api } from '@/api/client'

export interface SocTenantOption {
  id: number
  name: string
  environment: string
  /** Já existe time de SOC neste tenant — a tela desabilita em vez de deixar
   *  tentar e receber 409 (o índice `uq_nt_soc_tenant` permite um por tenant). */
  ja_tem_soc: boolean
  /** SOC habilitado pela plataforma neste tenant (`tenants.has_soc`). Falso
   *  não some da tela — desabilita com o motivo, porque `criar_time` recusa
   *  com 409 igual faz para `ja_tem_soc` (Task 1, `listar_tenants_da_organizacao`). */
  has_soc: boolean
}

export interface SocCatalogItem {
  key: string
  source: string
  rotulo: string
  severity_min: number
  /** Disparos da FONTE na janela, não deste item — a agregação é por `source`.
   *  O rótulo na tela diz isso; ver `soc_catalog.listar_com_historico`. */
  hits_da_fonte: number
  ultima_vez: string | null
  fonte_nunca_entregou: boolean
  /** A fonte emite evento de recuperação para este item? Decide se o campo
   *  "Religa após" faz sentido: sem recuperação, o alarme reabriria para
   *  sempre. Zabbix sim; Wazuh não (0 recuperações em 30 dias). */
  tem_recuperacao: boolean
}

export interface SocPolicy {
  id?: number
  catalog_key: string
  entry_level: number
  escalate_after_min: number | null
  /** Prazo do RECONHECIMENTO, não o do escalonamento (TASK-10779): se ninguém
   *  resolver em N minutos, o sistema liga de novo para quem reconheceu.
   *  `null` = o reconhecimento é terminal, que é o comportamento anterior.
   *  Só tem efeito onde o item do catálogo tem `tem_recuperacao`. */
  reack_after_min: number | null
  broadcast_all: boolean
  is_active?: boolean
  routing_rule_id?: number | null
}

/** Um alerta que ainda pede resposta de alguém do time.
 *
 *  Até 14/09/2026 nenhuma tela mostrava isto: o `feed.py` calculava
 *  `aberto`/`reconhecido` e nada no frontend consumia. Um alarme podia estar
 *  no nível 2 há 40 minutos e não havia onde ver. */
export interface SocEscalation {
  event_id: number
  occurred_at: string
  team_id: number
  /** Degrau em que o alerta está agora. Com `state='exhausted'` é o topo. */
  current_level: number
  /** `pending` ninguém pegou · `acked` alguém assumiu · `exhausted` chegou ao
   *  topo. `resolved` nunca aparece aqui: a tela é para o que pede ação. */
  state: 'pending' | 'acked' | 'exhausted'
  /** Quando o alarme volta a agir. Com o topo insistente, é quando toca de
   *  novo. `null` só enquanto a política não escalona. */
  next_due_at: string | null
  titulo: string
  severity: string
  source: string
  aberto_desde: string
  reconhecido_por: number | null
  acked_at: string | null
}

export interface SocUnclassified {
  catalog_key: string
  first_seen: string
  last_seen: string
  hits: number
}

export interface SocMembersResult {
  counts: Record<string, number>
  /** Quem está no time e NÃO tem inscrição de push em alguma fonte que o time
   *  assinou. Aviso, não erro: a inscrição é por fonte, em Notificações ›
   *  Preferências, e só a própria pessoa pode dar. Sem ela o alarme não toca.
   *
   *  NÃO é a permissão do navegador (`web_push_subscriptions`): são tabelas
   *  diferentes, e o texto que confundia as duas mandava a pessoa conferir o
   *  aparelho, onde não havia nada errado (TASK-10284). */
  sem_opt_in: string[]
  /** As fontes com lacuna, derivadas das políticas do time. Existe para o aviso
   *  poder NOMEAR o que ligar; vazio quando não há lacuna nenhuma. */
  fontes_sem_opt_in: string[]
}

export interface SocCandidato {
  id: number
  email: string
  full_name: string | null
  /** Nivel em que este candidato JA ESTA no time hoje; `null` para quem nao
   *  esta. `definir_membros` e substituicao total (DELETE + INSERT, nao
   *  merge) — sem este campo o editor nao tem como saber quem ja esta no
   *  time antes de salvar, e salvar do jeito que abriu apagaria todo mundo. */
  nivel_atual: number | null
}

/** Membro ATUAL do time que nao aparece em `items` — perdeu acesso ao tenant
 *  ou foi desativado. Preserva-lo na gravacao nao e possivel (`definir_membros`
 *  recusa por membership); o editor usa isto para AVISAR antes de salvar que
 *  essa pessoa some do time — sem o aviso, ela desaparecia em silencio no
 *  proximo "Salvar integrantes". */
export interface SocCandidatoInelegivel {
  id: number
  email: string
  full_name: string | null
}

export interface SocTeam {
  id: number
  name: string
  organization_id: number
  tenant_id: number
  tenant_name: string
  tenant_environment: string
  /** Conta AWS do tenant; `null` quando o tenant nao tem conta. */
  tenant_account_id: string | null
  /** Organizacoes que contem o tenant HOJE — a mesma derivacao do gate por
   *  time. Vazia = tenant fora de qualquer organizacao: so a plataforma
   *  administra o time. `organization_id` e so o registro de origem
   *  (TASK-11546). */
  organizacoes_do_tenant: number[]
  /** Autoridade sobre cobertura: `has_soc` do tenant desliga o time (e as
   *  regras) sem apagar o registro. Time desligado CONTINUA na lista — sumir
   *  é o defeito que a Fase 5b/Task 5b.1 consertou —, mas não pode aparecer
   *  como coberto. */
  is_active: boolean
  /** Teto de ligações por hora, por plantonista. `null` = sem teto, que é o
   *  comportamento anterior à TASK-10762 (e NÃO "voz desligada"). */
  max_ligacoes_por_hora?: number | null
  /** Contagem por nível, chave string ('1'|'2'|'3'). Nível ausente do time
   *  ainda entra com 0 — o servidor sempre devolve os três níveis. */
  members_by_level: Record<string, number>
}

export const socApi = {
  listarTimes: () =>
    api.get<{ items: SocTeam[] }>('/v1/admin/soc/teams').then((r) => r.data),


  tenantsDaOrg: (orgId: number) =>
    api
      .get<{ items: SocTenantOption[] }>(`/v1/admin/soc/organizations/${orgId}/tenants`)
      .then((r) => r.data.items),

  nomeSugerido: (tenantId: number) =>
    api
      .get<{ name: string }>(`/v1/admin/soc/tenants/${tenantId}/suggested-name`)
      .then((r) => r.data.name),

  criarTime: (payload: { organization_id: number; tenant_id: number; name?: string }) =>
    api
      .post<{ id: number; name: string }>('/v1/admin/soc/teams', payload)
      .then((r) => r.data),

  definirMembros: (teamId: number, levels: Record<string, number[]>) =>
    api
      .put<SocMembersResult>(`/v1/admin/soc/teams/${teamId}/members`, { levels })
      .then((r) => r.data),

  // So quem tem `user_tenants` no tenant deste time: e o mesmo teto que
  // `definirMembros` aplica na gravacao (ver alert_admin_routes.py). Oferecer
  // na tela quem a gravacao recusaria e o defeito que a Fase 5a ja consertou
  // uma vez (seletor stub).
  candidatos: (teamId: number) =>
    api
      .get<{
        items: SocCandidato[]
        inelegiveis: SocCandidatoInelegivel[]
        /** Da organizacao do tenant, ativo, sem acesso a ele e fora do time —
         *  quem o editor nao oferece, e precisa dizer por que (TASK-11547). */
        sem_acesso: SocCandidatoInelegivel[]
      }>(
        `/v1/admin/soc/teams/${teamId}/candidatos`,
      )
      .then((r) => r.data),

  catalogo: (teamId: number, dias = 90) =>
    api
      .get<{ items: SocCatalogItem[] }>(`/v1/admin/soc/teams/${teamId}/catalog`, {
        params: { dias },
      })
      .then((r) => r.data.items),

  politicas: (teamId: number) =>
    api
      .get<{ items: SocPolicy[] }>(`/v1/admin/soc/teams/${teamId}/policies`)
      .then((r) => r.data.items),

  salvarPoliticas: (teamId: number, items: SocPolicy[]) =>
    api
      .put<{ policies: number; rules: number }>(
        `/v1/admin/soc/teams/${teamId}/policies`,
        { items },
      )
      .then((r) => r.data),

  /** Teto de ligações por hora do time. `null` remove o teto. */
  salvarTeto: (teamId: number, max: number | null) =>
    api
      .put<{ max_ligacoes_por_hora: number | null }>(
        `/v1/admin/soc/teams/${teamId}/teto`,
        { max_ligacoes_por_hora: max },
      )
      .then((r) => r.data),

  /**
   * Quantas ligações este teto teria gerado nos últimos 30 dias.
   *
   * É a CONSEQUÊNCIA do número, e é o que a tela mostra abaixo do campo —
   * escolher "3" no escuro é diferente de ver o que "3" significa para este
   * time, com os eventos reais dele.
   */
  simulacaoDeTeto: (teamId: number, n: number) =>
    api
      .get<{
        teto: number
        dias: number
        sem_teto: number
        com_teto: number
        politicas_ativas: number
      }>(`/v1/admin/soc/teams/${teamId}/simulacao-de-teto`, { params: { n } })
      .then((r) => r.data),

  naoClassificados: (teamId: number) =>
    api
      .get<{ items: SocUnclassified[] }>(`/v1/admin/soc/teams/${teamId}/unclassified`)
      .then((r) => r.data.items),

  reconhecer: (eventId: number, payload: { team_id: number; occurred_at: string }) =>
    api
      .post(`/v1/soc/alerts/${eventId}/ack`, payload)
      .then((r) => r.data),

  escalonamentos: (teamId: number) =>
    api
      .get<{ items: SocEscalation[] }>(`/v1/admin/soc/teams/${teamId}/escalations`)
      .then((r) => r.data.items),

  /**
   * Encerra o alerta. É a ÚNICA coisa que para o alarme.
   *
   * Não confundir com `reconhecer`: aquele diz "estou cuidando" e compra o
   * prazo do reack; este diz "acabou". Para um alerta cuja fonte nunca manda
   * recuperação — o Wazuh teve 0 em 30 dias — é a única saída.
   */
  resolver: (teamId: number, eventId: number) =>
    api
      .post(`/v1/admin/soc/teams/${teamId}/escalations/${eventId}/resolver`)
      .then((r) => r.data),
}
